/* GemSense product-page add-ons.

   Two independent controllers that both need the same thing — how many pieces
   are in the cart right now:

     Gift wrap   a checkbox that keeps a Rs 100 wrap line matched, piece for
                 piece, to what the shopper is actually buying.
     Volume tier a read-only mirror of the quantity ladder configured in the
                 GoKwik panel. It never applies a discount and never prints a
                 price; GoKwik owns both.

   Each initialises only if its own markup is on the page, so either block can
   be removed from the product template without breaking the other.

   The pure helpers at the top are exported for tools/tests/pdp-addons.test.js. */
(function (root, factory) {
  'use strict';
  /* Both blocks ask for this file. Same URL, so it is fetched once, but a
     second <script> tag still runs it a second time — which would bind every
     listener twice. First one in wins. */
  if (root.GSPdpAddons) {
    if (typeof module !== 'undefined' && module.exports) module.exports = root.GSPdpAddons;
    return;
  }
  var api = factory();
  root.GSPdpAddons = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  /* The shipped ladder. The block schema carries the same numbers as editable
     defaults; a test fails if the two ever disagree. */
  var DEFAULT_TIERS = [
    { min: 1, pct: 0, label: '1 piece', sub: 'Standard', badge: '' },
    { min: 2, pct: 5, label: '2–4 pcs', sub: 'Save 5%', badge: 'POPULAR' },
    { min: 5, pct: 10, label: '5+ pcs', sub: 'Save 10%', badge: 'BEST VALUE' }
  ];

  /* =====================================================================
     Pure helpers
     ================================================================== */

  /* Index of the highest rung the count has reached. Below the first rung
     still reads as the first rung: a shopper with an empty cart is looking at
     standard pricing, not at nothing. */
  function tierFor(qty, tiers) {
    var i = 0;
    for (var k = 0; k < tiers.length; k++) {
      if (qty >= tiers[k].min) i = k;
    }
    return i;
  }

  /* What it would take to reach the next rung, or null at the top. */
  function nudgeFor(qty, tiers) {
    var here = tierFor(qty, tiers);
    var next = tiers[here + 1];
    if (!next) return null;
    var needed = next.min - qty;
    return { needed: needed < 1 ? 1 : needed, pct: next.pct };
  }

  /* "more" only makes sense once there is something to have more of. */
  function nudgeText(needed, pct, hasItems) {
    var noun = needed === 1 ? 'piece' : 'pieces';
    return 'Add ' + needed + (hasItems ? ' more ' : ' ') + noun + ' and save ' + pct + '%';
  }

  function unitPrice(item) {
    if (typeof item.final_price === 'number') return item.final_price;
    if (typeof item.price === 'number') return item.price;
    return 0;
  }

  /* Pieces the shopper is being charged to wrap.

     The wrap line is excluded so it cannot pay to wrap itself, and free items
     are excluded so the Selenite plate that gs-cart-perks gives away above
     Rs 999 does not quietly add Rs 100 to the bill. */
  function wrapQty(cart, wrapVid) {
    var total = 0;
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].variant_id === wrapVid) continue;
      if (unitPrice(items[i]) <= 0) continue;
      total += items[i].quantity;
    }
    return total;
  }

  /* Pieces that count toward the ladder. Free items DO count here — GoKwik
     counts units, and a free plate is a unit. Only the wrap itself is dropped,
     since wrapping a bracelet is not buying a second bracelet. */
  function tierQty(cart, wrapVid) {
    var total = 0;
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].variant_id === wrapVid) continue;
      total += items[i].quantity;
    }
    return total;
  }

  /* What one look at the cart means for the wrap line.

     `state` is the shopper's standing intent, and it has three values, not two.
     A boolean cannot tell "ticked but not added yet" from "was added and the
     shopper deleted it" — both show an absent wrap line beside a full cart —
     so a boolean version re-adds the charge somebody just removed in the cart
     drawer. That was a real bug.

       null      no wrap wanted
       'pending' ticked before there was anything to wrap; apply once
       'on'      the wrap line is in the cart and should track the piece count

     Returns the next state, what the checkbox should show, and the quantity to
     write (null writes nothing). The cart wins whenever it disagrees with an
     'on' state, because the shopper edited it by hand. */
  function wrapPlan(state, have, eligible) {
    if (state !== 'pending' && state !== 'on') {
      return { state: null, checked: false, write: have > 0 ? 0 : null };
    }
    if (have > 0) {
      return { state: 'on', checked: true, write: have === eligible ? null : eligible };
    }
    if (state === 'pending') {
      return { state: 'pending', checked: true, write: eligible > 0 ? eligible : null };
    }
    /* 'on', yet the line is gone: removed in the drawer. Never put it back. */
    return { state: null, checked: false, write: null };
  }

  /* How many pieces a tap on rung `index` must add, or null when that rung is
     not something to reach for — it is the rung you are on, or one you have
     already passed. Cards only ever add: the quantity stepper sits directly
     above them and a mis-tap on a phone must never be able to empty a cart. */
  function stepTo(qty, tiers, index) {
    var tier = tiers[index];
    if (!tier) return null;
    var need = tier.min - qty;
    return need > 0 ? { need: need, pct: tier.pct } : null;
  }

  /* Which cart endpoint moves the wrap line from `have` to `want`.

     /cart/change.js can only move a line that is already in the cart; handed a
     variant that is not there it returns the cart untouched and reports no
     error, so the checkbox appears to do nothing at all. Creating the line
     needs /cart/add.js. */
  function cartOp(have, want, vid) {
    if (have === want) return null;
    if (have === 0) return { url: '/cart/add.js', body: { items: [{ id: vid, quantity: want }] } };
    return { url: '/cart/change.js', body: { id: String(vid), quantity: want } };
  }

  var api = {
    DEFAULT_TIERS: DEFAULT_TIERS,
    cartOp: cartOp,
    stepTo: stepTo,
    wrapPlan: wrapPlan,
    tierFor: tierFor,
    nudgeFor: nudgeFor,
    nudgeText: nudgeText,
    wrapQty: wrapQty,
    tierQty: tierQty
  };

  if (typeof document === 'undefined') return api;

  /* =====================================================================
     Cart access, shared by both controllers
     ================================================================== */

  var listeners = [];
  var lastCart = null;
  var inFlight = null;

  /* Once the shopper commits to checkout, this file stops touching the cart.
     GoKwik reads the cart when its button is pressed; a reconcile landing in
     that window could change what is being paid for after the shopper has
     decided. Nothing here unfreezes it — a checkout either proceeds or the
     page is left. */
  var frozen = false;
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('.gokwik-checkout, #gokwik-buy-now, .shopifycheckoutbtn, [name="checkout"], .btn-checkout-dynamic')) {
      frozen = true;
    }
  }, true);

  function readCart() {
    if (inFlight) return inFlight;
    inFlight = fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        inFlight = null;
        lastCart = cart;
        for (var i = 0; i < listeners.length; i++) listeners[i](cart);
        return cart;
      })
      .catch(function () { inFlight = null; return lastCart; });
    return inFlight;
  }

  function onCart(fn) {
    listeners.push(fn);
    if (lastCart) fn(lastCart);
  }

  /* The cart drawer is server-rendered into the page, so a write that does not
     re-render it leaves the shopper's next drawer open showing a stale cart.
     The theme's own stepper solves this the same way. */
  function renderMinicart(res) {
    if (!res || !res.sections || !res.sections['minicart-form']) return;
    var host = document.getElementById('minicart-form');
    if (!host) return;
    var doc = new DOMParser().parseFromString(res.sections['minicart-form'], 'text/html');
    var fresh = doc.querySelector('#minicart-form');
    if (fresh) host.innerHTML = fresh.innerHTML;
  }

  /* The theme's own quantity stepper writes straight to /cart/change.js and
     dispatches nothing, so there is no event to listen for. Watching the
     number it prints catches every path that moves it — the stepper, an
     add-to-cart, or anything added later — without this file having to know
     any of them by name. */
  /* Edits made inside the cart drawer — deleting the wrap, changing a
     quantity — re-render this container. Watching it means the checkbox
     reflects a deletion straight away instead of waiting for some other event
     to happen along. */
  function watchDrawer() {
    var host = document.getElementById('minicart-form');
    if (!host) return;
    var timer = null;
    new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(readCart, 400);
    }).observe(host, { childList: true, subtree: true });
  }

  function watchStepper() {
    var vals = document.querySelectorAll('.gs-qty-val');
    if (!vals.length) return;
    var timer = null;
    var obs = new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(readCart, 350);
    });
    for (var i = 0; i < vals.length; i++) {
      obs.observe(vals[i], { childList: true, characterData: true, subtree: true });
    }
  }

  ['cart:updated', 'ajaxProduct:added', 'cart:refresh'].forEach(function (evt) {
    document.addEventListener(evt, function () { readCart(); });
  });

  /* =====================================================================
     Gift wrap
     ================================================================== */

  var INTENT_KEY = 'gs_giftwrap_intent';

  function readIntent() {
    try {
      var v = sessionStorage.getItem(INTENT_KEY);
      return v === 'pending' || v === 'on' ? v : null;
    } catch (e) { return null; }
  }
  function writeIntent(state) {
    try {
      if (state) sessionStorage.setItem(INTENT_KEY, state);
      else sessionStorage.removeItem(INTENT_KEY);
    } catch (e) { /* private mode: the checkbox still works, it just forgets */ }
  }

  function initGiftWrap(el) {
    var wrapVid = parseInt(el.getAttribute('data-wrap-variant'), 10);
    var cb = el.querySelector('[data-gs-gw-cb]');
    if (!wrapVid || !cb) return;

    var priceEl = el.querySelector('[data-gs-gw-price]');
    var unitPriceText = priceEl ? priceEl.textContent.trim() : '';
    var busy = false;
    var seeded = false;
    var state = readIntent();

    /* The price is per piece. Left alone it would read "+Rs 100" beside a cart
       being charged Rs 500, which is the kind of small lie a shopper finds at
       checkout. Liquid rendered the unit price; the multiplier is the only
       part that has to move. */
    function showPrice(pieces) {
      if (!priceEl) return;
      priceEl.textContent = pieces > 1 ? unitPriceText + ' \u00d7 ' + pieces : unitPriceText;
    }

    function setBusy(on) {
      busy = on;
      el.classList.toggle('is-busy', on);
      cb.disabled = on;
    }

    function lineQty(cart) {
      var items = (cart && cart.items) || [];
      for (var i = 0; i < items.length; i++) {
        if (items[i].variant_id === wrapVid) return items[i].quantity;
      }
      return 0;
    }

    /* Reconcile one cart observation against the standing intent. Every write
       to the wrap line goes through here, so there is one place that decides. */
    function settle(cart) {
      if (busy || frozen) return;

      var have = lineQty(cart);
      var eligible = wrapQty(cart, wrapVid);
      showPrice(eligible);

      /* First look of the session seeds the intent from the cart, so a reload
         with wrap already in the basket does not read as "not wanted" and
         delete it. */
      if (!seeded) {
        seeded = true;
        if (have > 0 && !state) { state = 'on'; writeIntent(state); }
      }

      var plan = wrapPlan(state, have, eligible);
      if (plan.state !== state) { state = plan.state; writeIntent(state); }
      cb.checked = plan.checked;
      if (plan.write === null) return;

      var op = cartOp(have, plan.write, wrapVid);
      if (!op) return;

      setBusy(true);
      fetch(op.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(Object.assign({}, op.body, {
          sections: 'minicart-form',
          sections_url: window.location.pathname
        }))
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          /* /cart/add.js answers with the line it created, not the cart, so
             the cart has to be re-read rather than inferred. */
          renderMinicart(res);
          setBusy(false);
          return readCart();
        })
        .then(function (fresh) {
          if (!fresh) return;
          document.querySelectorAll('.cart-count').forEach(function (n) {
            n.textContent = fresh.item_count;
          });
        })
        .catch(function () {
          setBusy(false);
          /* Put the checkbox back where the cart actually is, rather than
             leaving it claiming something that did not happen. */
          readCart().then(function (c) { if (c) cb.checked = lineQty(c) > 0; });
        });
    }

    onCart(settle);

    cb.addEventListener('change', function () {
      /* A tick is always 'pending': settle promotes it to 'on' once the line
         is actually observed in the cart. One path in, one path out. */
      state = cb.checked ? 'pending' : null;
      writeIntent(state);
      if (lastCart) settle(lastCart);
      else readCart();
    });
  }

  /* =====================================================================
     Volume tiers
     ================================================================== */

  function initTiers(el) {
    var tiers;
    try { tiers = JSON.parse(el.getAttribute('data-gs-tiers')); } catch (e) { tiers = null; }
    if (!tiers || !tiers.length) tiers = DEFAULT_TIERS;

    var wrapVid = parseInt(el.getAttribute('data-wrap-variant'), 10) || 0;
    var cards = el.querySelectorAll('[data-gs-vt-card]');
    var nudge = el.querySelector('[data-gs-vt-nudge]');
    var nudgeText_ = el.querySelector('[data-gs-vt-nudge-text]');
    var adding = false;

    /* Read at click time, not at init: the shopper may have changed variant
       since the page loaded. */
    function variantId() {
      var input = document.querySelector('.product-variant-id');
      return (input && parseInt(input.value, 10)) || 0;
    }

    /* /cart/add.js increments a line that already exists, so adding the
       shortfall is exactly "top the cart up to this rung". */
    function bump(need) {
      var vid = variantId();
      if (adding || frozen || need < 1 || !vid) return;
      adding = true;
      el.classList.add('is-busy');
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: vid, quantity: need }],
          sections: 'minicart-form',
          sections_url: window.location.pathname
        })
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          renderMinicart(res);
          adding = false;
          el.classList.remove('is-busy');
          return readCart();
        })
        .then(function (fresh) {
          if (!fresh) return;
          document.querySelectorAll('.cart-count').forEach(function (n) {
            n.textContent = fresh.item_count;
          });
          /* The theme's own stepper learns the quantity from its own events;
             without this it sits showing a stale number beside a fuller cart. */
          document.dispatchEvent(new CustomEvent('cart:updated'));
        })
        .catch(function () {
          adding = false;
          el.classList.remove('is-busy');
          readCart();
        });
    }

    for (var c = 0; c < cards.length; c++) {
      (function (card, index) {
        card.addEventListener('click', function () {
          var step = stepTo(tierQty(lastCart || { items: [] }, wrapVid), tiers, index);
          if (step) bump(step.need);
        });
      })(cards[c], c);
    }

    onCart(function (cart) {
      var qty = tierQty(cart, wrapVid);
      var here = tierFor(qty, tiers);
      var reached = qty >= tiers[0].min;

      for (var i = 0; i < cards.length; i++) {
        var card = cards[i];
        var isActive = reached && i === here;
        var step = stepTo(qty, tiers, i);
        var hint = card.querySelector('[data-gs-vt-add]');

        card.classList.toggle('is-active', isActive);
        card.classList.toggle('is-actionable', !!step);
        card.disabled = !step;

        if (hint) {
          hint.textContent = step ? '+' + step.need : '';
          hint.hidden = !step;
        }

        /* The first rung saves nothing, so "and save 0%" would be nonsense
           read aloud. */
        card.setAttribute('aria-label', step
          ? 'Add ' + step.need + ' to reach ' + tiers[i].label
            + (step.pct > 0 ? ' and save ' + step.pct + '%' : '')
          : tiers[i].label + (isActive ? ', your current quantity' : ''));
        if (isActive) card.setAttribute('aria-current', 'true');
        else card.removeAttribute('aria-current');
      }

      if (!nudge || !nudgeText_) return;
      var next = nudgeFor(qty, tiers);
      if (next) {
        nudgeText_.textContent = nudgeText(next.needed, next.pct, qty > 0);
        nudge.classList.remove('is-topped');
      } else {
        nudgeText_.textContent = 'Best value unlocked — ' + tiers[here].pct + '% off every piece';
        nudge.classList.add('is-topped');
      }
    });
  }

  /* =====================================================================
     Boot
     ================================================================== */

  function boot() {
    var wrap = document.querySelector('[data-gs-giftwrap]');
    var tiers = document.querySelector('[data-gs-tiers]');
    if (!wrap && !tiers) return;
    if (wrap) initGiftWrap(wrap);
    if (tiers) initTiers(tiers);
    watchStepper();
    watchDrawer();
    readCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  return api;
});
